import { BlockPreviewContext } from "#/_packages/umbrella/features/previews/block-preview-context";
import type {
	AccordionUmbBlockSettingsElementModel,
	AlertUmbBlockSettingsElementModel,
	ApiBlockGridItemModel,
	ApiBlockItemModel,
	ButtonUmbBlockSettingsElementModel,
	CarouselUmbBlockSettingsElementModel,
	CollapsibleUmbBlockSettingsElementModel,
	ContactCardUmbBlockSettingsElementModel,
	ContentFeedUmbBlockSettingsElementModel,
	DividerUmbBlockSettingsElementModel,
	DrawerButtonUmbBlockSettingsElementModel,
	DropdownUmbBlockSettingsElementModel,
	EmbedUmbBlockSettingsElementModel,
	FormUmbBlockSettingsElementModel,
	GalleryUmbBlockSettingsElementModel,
	GoogleTranslateLanguageSelectorUmbBlockSettingsElementModel,
	HeroUmbBlockSettingsElementModel,
	IconUmbBlockSettingsElementModel,
	ImageAndTextUmbBlockSettingsElementModel,
	ImageUmbBlockSettingsElementModel,
	KeyFigureUmbBlockSettingsElementModel,
	LanguageSelectorUmbBlockSettingsElementModel,
	LinkListUmbBlockSettingsElementModel,
	LoginLogoutUmbBlockSettingsElementModel,
	LogoUmbBlockSettingsElementModel,
	MegaMenuUmbBlockSettingsElementModel,
	NestedLayoutUmbBlockSettingsElementModel,
	SearchBarUmbBlockSettingsElementModel,
	SearchFieldUmbBlockSettingsElementModel,
	SpacerUmbBlockSettingsElementModel,
	SubmenuUmbBlockSettingsElementModel,
	TableOfContentsUmbBlockSettingsElementModel,
	TabsUmbBlockSettingsElementModel,
	TeaserUmbBlockSettingsElementModel,
	TestimonialUmbBlockSettingsElementModel,
	TextUmbBlockSettingsElementModel,
	VideoUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { Alert } from "#/_packages/umbrella/features/alerts/components/alert.tsx";
import AlertUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/alert-block.tsx";
import ButtonUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/button-umb-block.tsx";
import CustomizableLinkUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/customizable-link-umb-block.tsx";
import DividerUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/divider-block.tsx";
import DrawerButtonUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/drawer-button-block.tsx";
import DropdownUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/dropdown-block.tsx";
import EmbedUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/embed-block.tsx";
import GoogleTranslateLanguageSelectorUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/google-language-selector-block.tsx";
import ImageAndTextUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/image-and-text-block.tsx";
import ImageUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/image-block.tsx";
import LanguageSelectorUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/language-selector-block.tsx";
import LinkListUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/link-list-block.tsx";
import LoginLogoutUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/login-logout-block.tsx";
import LogoUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/logo-block.tsx";
import MegaMenuUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/mega-menu-block.tsx";
import NestedLayoutUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/nested-layout-block.tsx";
import SearchBarUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/search-bar-block.tsx";
import SearchFieldUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/search-field-block.tsx";
import { ElementTypes } from "#/_packages/umbrella/integrations/umbraco/utils/element-types.ts";
import {
	type BlockGridItemRendererMapping,
	type BlockGridItemRendererProps,
	useUmbracoRendererMappings,
} from "#/_packages/umbrella/integrations/umbraco/utils/renderer-mappings.tsx";
import ContactCardUmbBlock from "../components/blocks/contact-card-block";
import ContentFeedUmbBlock from "../components/blocks/content-feed-block";
import HeroUmbBlock from "../components/blocks/hero-block";
import IconUmbBlock from "../components/blocks/icon-block";
import SectionUmbBlock from "../components/blocks/section-block";
import SpacerUmbBlock from "../components/blocks/spacer-block";
import { TableOfContentsUmbBlock } from "../components/blocks/table-of-contents-block";
import { TeaserUmbBlock } from "../components/blocks/teaser-block";
import TestimonialUmbBlock from "../components/blocks/testimonial-block";
import TextUmbBlock from "../components/blocks/text-block";
import {
	optionalBlockModules,
	RendererModuleBoundary,
} from "./renderer-modules";

// Resolve optional registries during render: another split server chunk may
// initialize them after this dispatch module has been evaluated.

type HeaderModel = {
	drawerSections: HeaderDrawerSection[];
};

type HeaderDrawerSection = {
	id: string;
	order: number;
	block: ApiBlockGridItemModel;
};

type HeaderDrawerSettings = {
	drawerShowInDrawer?: boolean | null;
	drawerPriority?: number | null;
};

const headerDrawerBlockIds: Partial<Record<string, string>> = {
	megaMenuUmbBlock: "navigation",
	searchFieldUmbBlock: "search",
	drawerButtonUmbBlock: "drawerButton",
	buttonUmbBlock: "button",
	dropdownUmbBlock: "dropdown",
	logoUmbBlock: "logo",
	iconUmbBlock: "icon",
	imageUmbBlock: "image",
	languageSelectorUmbBlock: "languageSelector",
	keyFigureUmbBlock: "keyFigure",
	linkListUmbBlock: "linkList",
	loginLogoutUmbBlock: "loginLogout",
	spacerUmbBlock: "spacer",
	teaserUmbBlock: "teaser",
	testimonialUmbBlock: "testimonial",
	textUmbBlock: "text",
	videoUmbBlock: "video",
	dividerUmbBlock: "divider",
};

export function BuildHeaderModel(
	headerBlocks?: ApiBlockGridItemModel[],
): HeaderModel {
	const drawerSections: HeaderDrawerSection[] = [];

	for (const block of headerBlocks ?? []) {
		collectHeaderDrawerSections(block, drawerSections);
	}

	return {
		drawerSections: drawerSections.sort(
			(left, right) => left.order - right.order,
		),
	};
}

function collectHeaderDrawerSections(
	block: ApiBlockGridItemModel,
	sections: HeaderDrawerSection[],
) {
	const id = headerDrawerBlockIds[block.content.contentType];
	const settings = block.settings?.properties as
		| HeaderDrawerSettings
		| undefined;

	if (id && settings?.drawerShowInDrawer) {
		sections.push({
			id,
			order: settings.drawerPriority ?? 0,
			block,
		});
	}

	if (
		block.content.contentType === ElementTypes.NestedLayout &&
		"properties" in block.content
	) {
		for (const nestedItem of block.content.properties?.blockGrid?.items ?? []) {
			collectHeaderDrawerSections(nestedItem, sections);
		}
	}

	for (const area of block.areas ?? []) {
		for (const nestedItem of area.items ?? []) {
			collectHeaderDrawerSections(nestedItem, sections);
		}
	}
}

function DefaultBlockGridItemRenderer({
	model,
	options = { isHeaderDrawer: false },
	className,
}: BlockGridItemRendererProps) {
	switch (model.content.contentType) {
		case ElementTypes.Accordion:
			return (
				<optionalBlockModules.accordionUmbBlock.Renderer
					content={model.content}
					settings={model.settings as AccordionUmbBlockSettingsElementModel}
					className={className}
				/>
			);

		case ElementTypes.Alert:
			return (
				<AlertUmbBlock
					content={model.content}
					settings={model.settings as AlertUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Button:
			return (
				<ButtonUmbBlock
					isHeaderDrawer={options.isHeaderDrawer}
					content={model.content}
					settings={model.settings as ButtonUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Carousel:
			return (
				<optionalBlockModules.carouselUmbBlock.Renderer
					content={model.content}
					settings={model.settings as CarouselUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Collapsible:
			return (
				<optionalBlockModules.collapsibleUmbBlock.Renderer
					content={model.content}
					settings={model.settings as CollapsibleUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.ContactCard:
			return (
				<ContactCardUmbBlock
					content={model.content}
					settings={model.settings as ContactCardUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.ContentFeed:
			return (
				<ContentFeedUmbBlock
					content={model.content}
					settings={model.settings as ContentFeedUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.CustomizableLink:
			return (
				<CustomizableLinkUmbBlock
					content={model.content}
					className={className}
				/>
			);
		case ElementTypes.Divider:
			return (
				<DividerUmbBlock
					content={model.content}
					settings={model.settings as DividerUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.DrawerButton:
			return (
				<DrawerButtonUmbBlock
					content={model.content}
					settings={model.settings as DrawerButtonUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Dropdown:
			return (
				<DropdownUmbBlock
					content={model.content}
					settings={model.settings as DropdownUmbBlockSettingsElementModel}
					isHeaderDrawer={options.isHeaderDrawer}
					className={className}
				/>
			);
		case ElementTypes.Embed:
			return (
				<EmbedUmbBlock
					content={model.content}
					settings={model.settings as EmbedUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.GoogleTranslateLanguageSelector:
			return (
				<GoogleTranslateLanguageSelectorUmbBlock
					content={model.content}
					settings={
						model.settings as GoogleTranslateLanguageSelectorUmbBlockSettingsElementModel
					}
					className={className}
				/>
			);
		case ElementTypes.Form:
			return (
				<optionalBlockModules.formUmbBlock.Renderer
					content={model.content}
					settings={model.settings as FormUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Gallery:
			return (
				<optionalBlockModules.galleryUmbBlock.Renderer
					content={model.content}
					settings={model.settings as GalleryUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Hero:
			return (
				<HeroUmbBlock
					content={model.content}
					settings={model.settings as HeroUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Icon:
			return (
				<IconUmbBlock
					content={model.content}
					settings={model.settings as IconUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.ImageAndText:
			return (
				<ImageAndTextUmbBlock
					content={model.content}
					settings={model.settings as ImageAndTextUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Image:
			return (
				<ImageUmbBlock
					content={model.content}
					settings={model.settings as ImageUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.KeyFigure:
			return (
				<optionalBlockModules.keyFigureUmbBlock.Renderer
					content={model.content}
					settings={model.settings as KeyFigureUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.LanguageSelector:
			return (
				<LanguageSelectorUmbBlock
					content={model.content}
					settings={
						model.settings as LanguageSelectorUmbBlockSettingsElementModel
					}
					className={className}
				/>
			);
		case ElementTypes.LinkList:
			return (
				<LinkListUmbBlock
					content={model.content}
					settings={model.settings as LinkListUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.LoginLogout:
			return (
				<LoginLogoutUmbBlock
					content={model.content}
					settings={model.settings as LoginLogoutUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Logo:
			return (
				<LogoUmbBlock
					content={model.content}
					settings={model.settings as LogoUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.MegaMenu:
			return (
				<MegaMenuUmbBlock
					content={model.content}
					settings={model.settings as MegaMenuUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.NestedLayout:
			return (
				<NestedLayoutUmbBlock
					content={model.content}
					settings={model.settings as NestedLayoutUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.SearchBar:
			return (
				<SearchBarUmbBlock
					content={model.content}
					settings={model.settings as SearchBarUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.SearchField:
			return (
				<SearchFieldUmbBlock
					isHeaderDrawer={options.isHeaderDrawer}
					isPreview={options.isPreview}
					content={model.content}
					settings={model.settings as SearchFieldUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.OneColumnBlockGrid:
		case ElementTypes.OneTwoColumnBlockGrid:
		case ElementTypes.OneThreeColumnBlockGrid:
		case ElementTypes.OneTwoOneColumnBlockGrid:
		case ElementTypes.TwoColumnBlockGrid:
		case ElementTypes.TwoOneColumnBlockGrid:
		case ElementTypes.ThreeColumnBlockGrid:
		case ElementTypes.ThreeOneColumnBlockGrid:
		case ElementTypes.FourColumnBlockGrid:
			return <SectionUmbBlock model={model} className={className} />;
		case ElementTypes.Submenu:
			return (
				<optionalBlockModules.submenuUmbBlock.Renderer
					content={model.content}
					settings={model.settings as SubmenuUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Text:
			return (
				<TextUmbBlock
					content={model.content}
					settings={model.settings as TextUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Spacer:
			return (
				<SpacerUmbBlock
					content={model.content}
					settings={model.settings as SpacerUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.TableOfContents:
			return (
				<TableOfContentsUmbBlock
					content={model.content}
					settings={
						model.settings as TableOfContentsUmbBlockSettingsElementModel
					}
					className={className}
				/>
			);
		case ElementTypes.Tabs:
			return (
				<optionalBlockModules.tabsUmbBlock.Renderer
					content={model.content}
					settings={model.settings as TabsUmbBlockSettingsElementModel}
					className={className}
				/>
			);

		case ElementTypes.Teaser:
			return (
				<TeaserUmbBlock
					content={model.content}
					settings={model.settings as TeaserUmbBlockSettingsElementModel}
					className={className}
				/>
			);

		case ElementTypes.Testimonial:
			return (
				<TestimonialUmbBlock
					content={model.content}
					settings={model.settings as TestimonialUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		case ElementTypes.Video:
			return (
				<optionalBlockModules.videoUmbBlock.Renderer
					content={model.content}
					settings={model.settings as VideoUmbBlockSettingsElementModel}
					className={className}
				/>
			);
		default:
			return (
				<Alert intent="danger">
					Failed to find view for block: <b>{model.content.contentType}</b>
				</Alert>
			);
	}
}

export const defaultBlockGridItemTypes: Array<
	(typeof ElementTypes)[Exclude<keyof typeof ElementTypes, "prototype">]
> = [
	"accordionUmbBlock",
	"alertUmbBlock",
	"buttonUmbBlock",
	"carouselUmbBlock",
	"collapsibleUmbBlock",
	"contactCardUmbBlock",
	"contentFeedUmbBlock",
	"customizableLinkUmbBlockPartial",
	"dividerUmbBlock",
	"drawerButtonUmbBlock",
	"dropdownUmbBlock",
	"embedUmbBlock",
	"googleTranslateLanguageSelectorUmbBlock",
	"formUmbBlock",
	"galleryUmbBlock",
	"heroUmbBlock",
	"iconUmbBlock",
	"imageAndTextUmbBlock",
	"imageUmbBlock",
	"keyFigureUmbBlock",
	"languageSelectorUmbBlock",
	"linkListUmbBlock",
	"loginLogoutUmbBlock",
	"logoUmbBlock",
	"megaMenuUmbBlock",
	"nestedLayoutUmbBlock",
	"searchBarUmbBlock",
	"searchFieldUmbBlock",
	"oneColumnUmbBlockGrid",
	"oneTwoUmbBlockGrid",
	"oneThreeColumnUmbBlockGrid",
	"oneTwoOneColumnUmbBlockGrid",
	"twoColumnUmbBlockGrid",
	"twoOneColumnUmbBlockGrid",
	"threeColumnUmbBlockGrid",
	"threeOneColumnUmbBlockGrid",
	"fourColumnUmbBlockGrid",
	"submenuUmbBlock",
	"textUmbBlock",
	"spacerUmbBlock",
	"tableOfContentsUmbBlock",
	"tabsUmbBlock",
	"teaserUmbBlock",
	"testimonialUmbBlock",
	"videoUmbBlock",
];

export const defaultBlockGridItemRenderers: BlockGridItemRendererMapping =
	Object.fromEntries(
		defaultBlockGridItemTypes.map((contentType) => [
			contentType,
			DefaultBlockGridItemRenderer,
		]),
	);

export function PreviewBlockGridItemRenderer(
	props: BlockGridItemRendererProps,
) {
	const contentType = props.model.content.contentType;
	const Renderer = defaultBlockGridItemRenderers[contentType];

	if (!Renderer) {
		return (
			<Alert intent="danger">
				Failed to find view for block: <b>{contentType}</b>
			</Alert>
		);
	}

	return (
		<RendererModuleBoundary value={props.model}>
			<BlockPreviewContext value={true}>
				<Renderer {...props} options={{ ...props.options, isPreview: true }} />
			</BlockPreviewContext>
		</RendererModuleBoundary>
	);
}

export function BlockGridItemRenderer(props: BlockGridItemRendererProps) {
	const { blocks } = useUmbracoRendererMappings();
	const contentType = props.model.content.contentType;
	const CustomRenderer = blocks[contentType];
	if (CustomRenderer) return <CustomRenderer {...props} />;
	const Renderer = defaultBlockGridItemRenderers[contentType];

	if (!Renderer) {
		return (
			<Alert intent="danger">
				Failed to find view for block: <b>{contentType}</b>
			</Alert>
		);
	}

	return (
		<RendererModuleBoundary value={props.model}>
			<Renderer {...props} />
		</RendererModuleBoundary>
	);
}

export function RenderBlocks(blocks?: ApiBlockItemModel[]) {
	if (!blocks) return null;
	return blocks.map((block) => (
		<BlockGridItemRenderer key={block.content.id} model={block} />
	));
}
export function RenderBlock(block?: ApiBlockItemModel, className?: string) {
	if (!block) return null;
	return <BlockGridItemRenderer model={block} className={className} />;
}
